import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const evidenceStreams = [
  { label: '文本', sub: '内容和理论', desc: '判断内容是否有思想支撑', icon: 'ri-file-text-line', color: 'primary' },
  { label: '声音', sub: '节奏与表达', desc: '揭示表达节奏和情感推进', icon: 'ri-volume-up-line', color: 'accent' },
  { label: '画面', sub: '活动与呈现', desc: '说明故事、图片和活动怎样呈现', icon: 'ri-image-line', color: 'primary' },
];

const cases = [
  {
    code: '本科05',
    title: '学生侧相对认可候选',
    diff: '学生侧头部，专家侧中后部',
    detail: '只能说明相对认可差异，感染力原因需与课堂材料核验',
    warn: '人工差异已核',
  },
  {
    code: '高职05',
    title: '专家与学生差异候选',
    diff: '专家侧尾部，学生侧中部',
    detail: '双方差距较大，价值号召力和学生感受可能较强',
    warn: '人工差异已核',
  },
  {
    code: '高职06',
    title: '文化感染与专题统摄边界',
    diff: '待补证据',
    detail: '文化温度和学生接受度需与专题完整度共同判断',
    warn: '待补证据',
  },
];

export default function P30MultiModalNew({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12">
        <div ref={titleRef} className="text-center mb-5 md:mb-7">
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100/60 text-primary-700 text-[11px] font-body font-semibold">
              <i className="ri-scan-line text-xs" /> 技术点四
            </span>
          </div>
          <h2 className={`font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            多模态互证与<span className="text-primary-600">课堂感染力</span>
          </h2>
        </div>

        {/* 三束证据汇聚 */}
        <div className={`max-w-4xl mx-auto mb-6 transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="relative">
            {/* 连接中心 */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full glass-gold border-accent-200/40 flex items-center justify-center z-10">
              <span className="text-[10px] font-body font-bold text-foreground-700 text-center leading-tight">三类<br />互证</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {evidenceStreams.map((stream, i) => (
                <div key={stream.label} className={`p-4 rounded-2xl text-center transition-all duration-500 hover:scale-[1.03] ${stream.color === 'primary' ? 'glass-liquid border-primary-200/30' : 'glass-liquid-gold border-accent-200/30'}`}>
                  <div className="relative z-10">
                    <div className={`w-10 h-10 mx-auto mb-2 rounded-xl flex items-center justify-center ${stream.color === 'primary' ? 'glass-blue border-primary-200/40' : 'glass-gold border-accent-200/40'}`}>
                      <i className={`${stream.icon} text-base ${stream.color === 'primary' ? 'text-primary-600' : 'text-accent-600'}`} />
                    </div>
                    <p className="text-xs font-body font-bold text-foreground-800">{stream.label}</p>
                    <p className="text-[11px] text-foreground-500 font-body mt-0.5">{stream.sub}</p>
                    <p className="text-[10px] text-foreground-400 font-body mt-1">{stream.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 三张案例卡 */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto mb-5 transition-all duration-1000 delay-400 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {cases.map((c, i) => (
            <div key={c.code} className="p-4 rounded-2xl glass border-white/50 hover:border-primary-200/40 transition-all duration-500" style={{ transitionDelay: `${400 + i * 100}ms` }}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading text-sm font-bold text-foreground-800">{c.code}</h3>
                <span className="text-[10px] font-body font-semibold text-foreground-400">{c.warn}</span>
              </div>
              <p className="text-xs font-body font-semibold text-foreground-700 mb-1.5">{c.title}</p>
              <p className="text-[11px] text-foreground-500 font-body leading-relaxed mb-1.5">{c.diff}</p>
              <p className="text-[10px] text-foreground-400 font-body">{c.detail}</p>
            </div>
          ))}
        </div>

        {/* 数据 */}
        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-900 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl glass border-white/50">
            <i className="ri-bar-chart-line text-sm text-primary-500" />
            <span className="text-xs font-body text-foreground-500">
              本科感染力轨对专家面板与学生面板方向稳定率约<span className="font-bold text-primary-700">92%</span>和<span className="font-bold text-primary-700">95%</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}